import ErrorPage from "next/error";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { ReactElement, useEffect } from "react";
import Footnote from "src/components/atoms/Footnote";
import { BlockRenderer } from "src/components/molecules/BlockRenderer";
import LeadFormSection from "src/components/forms/LeadForm";
import Faq from "src/components/organisms/Faq";
import RichContent from "src/components/organisms/RichContent";
import TwoStackColumn from "src/components/organisms/TwoStackColumn";
import { PageSeo } from "src/components/SEO";
import siteMetadata from "src/data/siteMetadata";
import { IPageGenericPageFields } from "src/types/generated/contentful";
import ArticleSchema from "@src/components/schemas/ArticleSchema";
import Breadcrumbs from "@src/components/atoms/Breadcrumbs/Breadcrumbs";
import { PageProvider } from "@src/components/PageProvider";

declare global {
  interface Window {
    dataLayer: any[] | undefined;
    blueshift: any;
    blueshiftProductId: string | undefined;
  }
}

const ContentTypeToComponentMap = {
  richContent: RichContent,
  twoStackColumn: TwoStackColumn,
  faq: Faq,
};

export type GenericPagePageProps = {
  page: IPageGenericPageFields;
};

// TODO: add back the generic page props remove any

const GenericPage = (data: any): ReactElement => {
  const { page, settings, header, footerMenu, breadcrumbs } = data;
  const router = useRouter();

  const slug = page?.fields?.slug;
  if (!slug) {
    console.error("ERROR: no slug found", JSON.stringify(page));
  }
  const seoTitle = page?.fields?.seo?.fields?.title ?? page?.fields?.title;
  const seoDescription = page?.fields?.seo?.fields?.description;
  const seoImages = page?.fields?.seo?.fields?.images;
  const noIndex = page?.fields?.seo?.fields?.no_index || false;
  const noFollow = page?.fields?.seo?.fields?.no_follow || false;
  const video = page?.fields?.seo?.fields?.videos
    ? page?.fields?.seo?.fields?.videos[0]
    : undefined;

  const blueshiftProductId: string =
    page?.fields?.productType?.fields?.productId;

  useEffect(() => {
    if (typeof blueshiftProductId !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      if (window.blueshift) {
        window.dataLayer.push({
          event: "blueshiftPageView",
          blueshiftProductId,
        });
        console.log("Fired bsPageView:", blueshiftProductId);
      } else {
        window.dataLayer.push({
          blueshiftProductId,
        });
        console.log("Fired GA Only Err no BS:", blueshiftProductId);
      }
    }
  }, [blueshiftProductId]);

  if (!router.isFallback && !page) {
    return <ErrorPage statusCode={404} />;
  }

  if (router.isFallback) {
    return <div>Loading…</div>;
  }

  // console.log(
  //   "customContactForm",
  //   JSON.stringify(page?.fields?.content?.fields?.customContactForm?.fields)
  // );

  return (
    <PageProvider slug={slug}>
      <Head>
        <title>{seoTitle}</title>
      </Head>
      <PageSeo
        url={`${siteMetadata.mainSiteUrl}/${slug}`}
        title={seoTitle}
        description={seoDescription}
        images={seoImages}
        nofollow={noFollow}
        noindex={noIndex}
        video={video}
        datePublished={new Date(page?.sys?.createdAt).toISOString()}
        dateModified={new Date(page?.sys?.updatedAt).toISOString()}
        authorName={""}
      />
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <article>
        {/*
        TODO: removed article schema since it needs an author
        <ArticleSchema
          url={`${siteMetadata.mainSiteUrl}/blog/${slug}`}
          title={seoTitle}
          description={seoDescription}
          organization={settings?.organization}
          images={seoImages}
          dateModified={new Date(page?.sys?.updatedAt).toISOString()}
          datePublished={new Date(page?.sys?.createdAt).toISOString()}
          author={page?.fields?.seo?.fields?.author}
          authorPicture={page?.fields?.seo?.fields?.authorPicture}
          authorName={page?.fields?.seo?.fields?.author}
        />*/}
        {/*<PostHeader post={page} />*/}
        <div>
          <BlockRenderer
            block={page?.fields?.content?.fields?.content}
            layoutId={page["data-layoutid"]}
          />
        </div>

        {page?.fields?.content?.fields?.customContactForm ? (
          <BlockRenderer
            block={page?.fields?.content?.fields?.customContactForm}
            layoutId={"customContactForm"}
          />
        ) : (
          <LeadFormSection
            formVerticalAlignment={"Top"}
            formHorizontalAlignment={"Left"}
          />
        )}

        {page?.fields?.content?.fields?.footnote ? (
          <BlockRenderer
            block={page?.fields?.content?.fields?.footnote}
            layoutId={"footnote"}
          />
        ) : (
          <Footnote />
        )}
      </article>

      <footer>
        <BlockRenderer layoutId="footer" block={footerMenu} />
      </footer>
    </PageProvider>
  );
};

export default React.memo(GenericPage);
